import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, X } from "lucide-react";
import { API, getToken } from "@/lib/api";

export default function AIAssistant({ open, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm 02 (Zero Two), your TBX learning assistant. Ask me anything — course recommendations, study plans, deadlines, coding help, or how to use the platform." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: userMsg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ message: userMsg }),
      });
      if (!res.body) throw new Error("No stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const chunk of parts) {
          const line = chunk.replace(/^data:\s?/, "");
          if (line === "[DONE]") continue;
          if (line.startsWith("[ERROR]")) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please try again." };
              return copy;
            });
            continue;
          }
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: (copy[copy.length - 1].content || "") + line };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection error. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="ai-assistant-panel">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-[#F5F3FF] border-l border-violet-200 shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-violet-200 flex items-center gap-3 bg-white/60 backdrop-blur-xl">
          <div className="w-9 h-9 rounded-xl bg-violet-500 text-white flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-slate-900">02 — AI Assistant</div>
            <div className="text-xs text-violet-600 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500 pulse-dot" />
              Powered by Gemini 3 Flash · TrillionBytexedu
            </div>
          </div>
          <button data-testid="close-ai-btn" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="ai-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                m.role === "user" ? "bg-blue-600 text-white rounded-br-md" : "bg-white border border-violet-100 text-slate-800 rounded-bl-md"
              }`}>
                {m.content || (streaming && i === messages.length - 1 ? <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-violet-400 pulse-dot" /><span className="w-1.5 h-1.5 rounded-full bg-violet-400 pulse-dot" style={{ animationDelay: "0.2s" }} /><span className="w-1.5 h-1.5 rounded-full bg-violet-400 pulse-dot" style={{ animationDelay: "0.4s" }} /></span> : "")}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-violet-200 bg-white/60 backdrop-blur-xl">
          <div className="flex gap-2">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about a course, deadline, study tip…"
              className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-slate-200 outline-none focus:ring-2 focus:ring-violet-400 text-sm"
            />
            <button
              data-testid="ai-send-btn"
              onClick={send}
              disabled={streaming}
              className="px-4 rounded-xl bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
